import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Server, Cloud, Settings, Cpu, Zap } from 'lucide-react';
import { ANIMATION_VARIANTS, HERO_CONTENT } from '../constants';
import { 
  useFloatingParticles,
  usePerformanceMonitoring,
  useOptimizedBackground
} from '../hooks/useAnimations';
import { GlassCard, Marquee, StyledIcon, FeatureList } from '../components/animations';
import { handlePageNavigation, handleSectionNavigation } from '../utils/navigation';

const ServicesPage: React.FC = () => {
  const location = useLocation();
  usePerformanceMonitoring('ServicesPage');
  
  // Handle scrolling to section on page load if hash is present
  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.replace('#', '');
      handleSectionNavigation(sectionId);
    }
  }, [location.hash]);
  
  // Global floating particles for the entire page (mandatory pattern)
  const { particlesRef, containerClasses: particlesContainerClasses, particles } = useFloatingParticles(50);

  // Optimized backgrounds for alternating sections
  const { backgroundClasses: heroBackgroundClasses, backgroundStyle: heroBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: servicesBackgroundClasses, backgroundStyle: servicesBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  const { backgroundClasses: ctaBackgroundClasses, backgroundStyle: ctaBackgroundStyle } = useOptimizedBackground(
    HERO_CONTENT.backgroundImage,
    'linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.9))'
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={ANIMATION_VARIANTS.fadeIn}
      className="min-h-screen relative"
    >
      {/* Global Floating Particles Background */}
      <div ref={particlesRef} className={`${particlesContainerClasses} fixed inset-0 z-30`}>
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={particle.classes}
            style={particle.style}
          />
        ))}
      </div>

      {/* Page Content */}
      <div className="relative z-40">
        {/* Hero Section */}
        <section className="section-padding relative text-white">
          {/* Hero Background - Normal orientation */}
          <div 
            className={heroBackgroundClasses}
            style={heroBackgroundStyle}
          />
          
          <div className="container relative z-40 pt-24">
            {/* Header Content - Constrained width */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                variants={ANIMATION_VARIANTS.slideUp}
                className="flex items-center justify-center mb-6"
              >
                <StyledIcon 
                  icon={Cpu} 
                  size="xl" 
                  color="primary" 
                  variant="static"
                />
              </motion.div>
              
              <motion.h1
                variants={ANIMATION_VARIANTS.slideUp}
                className="heading-1 mb-6"
              >
                Our <span className="text-primary-gradient">Services</span>
              </motion.h1>
              
              <motion.p
                variants={ANIMATION_VARIANTS.slideUp}
                className="hero-subtitle max-w-3xl mx-auto"
              >
                Advanced GPU computing solutions and AI software services designed to power your 
                high-performance computing needs and accelerate your AI initiatives.
              </motion.p>
            </motion.div>
          </div>
          
          {/* Products Marquee - Full viewport width, outside container */}
          <motion.div 
            className="mt-12 relative w-screen -mx-4 md:-mx-8 lg:-mx-16 xl:-mx-20 z-40"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={ANIMATION_VARIANTS.fadeIn}
          >
            <Marquee speed="normal" expandOnHover={true} className="pt-8 mb-10">
              <div className="flex space-x-8 flex-shrink-0 pr-8">
                {/* First set - only increased height */}
                <div className="w-48 h-36 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                  <img
                    src="/assets/images/products/GPU1.png"
                    alt="GPU Solution"
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="w-48 h-36 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                  <img
                    src="/assets/images/products/GPU2.png"
                    alt="GPU Solution"
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="w-48 h-36 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                  <img
                    src="/assets/images/products/Server1.png"
                    alt="Server Solution"
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="w-48 h-36 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                  <img
                    src="/assets/images/products/Server2.png"
                    alt="Server Solution"
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="w-48 h-36 rounded-lg overflow-hidden shadow-lg flex-shrink-0">
                  <img
                    src="/assets/images/products/Server3.png"
                    alt="Server Solution"
                    className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity duration-300"
                    loading="lazy"
                  />
                </div>
              </div>
            </Marquee>
          </motion.div>
        </section>

        {/* Service Details - Main Focus */}
        <section className="relative py-8">
          {/* Details Background - Rotated orientation for alternating effect */}
          <div 
            className={servicesBackgroundClasses}
            style={{
              ...servicesBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={ANIMATION_VARIANTS.staggerChildren}
            >
              <motion.div variants={ANIMATION_VARIANTS.slideUp} className="text-center mb-12">
                <h2 className="heading-2 mb-6">Our Specialized <span className="text-primary-gradient">Services</span></h2>
                <p className="body-large max-w-3xl mx-auto text-gray-300">
                  Explore our comprehensive GPU computing and AI software services designed 
                  to deliver high-performance solutions for your business needs.
                </p>
              </motion.div>

              <div className="space-y-12">
                {/* Built-to-Suit GPU Compute Clusters */}
                <motion.div variants={ANIMATION_VARIANTS.slideUp} id="built-to-suit-gpu-clusters">
                  <GlassCard className="rounded-2xl p-8 border border-primary-500/20 hover:border-primary-400/30 transition-all duration-500 group">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div className="space-y-6">
                        <div className="flex items-center mb-6">
                          <div className="mr-4">
                            <StyledIcon 
                              icon={Server} 
                              size="md" 
                              color="primary" 
                              variant="default"
                            />
                          </div>
                          <h3 className="text-2xl font-semibold text-white-gradient">
                            Built-to-Suit GPU Compute Clusters
                          </h3>
                        </div>
                        
                        <p className="text-gray-300 leading-relaxed">
                          Custom-designed high-performance GPU computing clusters optimized for demanding 
                          computational workloads. Our solutions are tailored to your specific requirements, 
                          ensuring maximum performance and efficiency for your applications.
                        </p>
                        
                        <FeatureList 
                          features={[
                            "Custom hardware configuration based on workload requirements",
                            "Latest NVIDIA GPU technology for optimal performance",
                            "Professional installation and setup services",
                            "24/7 monitoring and maintenance support"
                          ]}
                          className="mb-6"
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <div className="relative group/image">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-transparent rounded-lg"></div>
                          <div className="absolute -inset-1 bg-gradient-to-r from-primary-500/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                          <img
                            src="/assets/images/services/Data_Center_Img.jpg"
                            alt="GPU Compute Clusters - Data Center"
                            className="relative rounded-lg shadow-lg w-full object-cover hover:scale-105 transition-transform duration-500 group-hover/image:shadow-primary-500/25"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        {/* Performance Stats */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-black/20 rounded-xl border border-primary-500/10">
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-primary-400 mb-1">99.9%</div>
                            <div className="text-xs text-gray-400">Uptime</div>
                          </div>
                          <div className="text-center border-x border-primary-500/20">
                            <div className="text-lg sm:text-2xl font-bold text-primary-400 mb-1">48TB</div>
                            <div className="text-xs text-gray-400">Max Memory</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-primary-400 mb-1">24/7</div>
                            <div className="text-xs text-gray-400">Support</div>
                          </div>
                        </div>
                        
                        {/* Learn More Button - Full Width */}
                        <Link 
                          to="/services/gpu-clusters" 
                          className="btn-futuristic w-full text-center flex items-center justify-center gap-2 group"
                          onClick={handlePageNavigation}
                        >
                          <span>Learn More</span>
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>

                {/* NVIDIA GPU Node Leasing */}
                <motion.div variants={ANIMATION_VARIANTS.slideUp} id="nvidia-gpu-node-leasing">
                  <GlassCard className="rounded-2xl p-8 border border-blue-500/20 hover:border-blue-400/30 transition-all duration-500 group">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div className="lg:order-1 space-y-4">
                        <div className="relative group/image">
                          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent rounded-lg"></div>
                          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                          <img
                            src="/assets/images/services/nvidia-gpu.jpg"
                            alt="NVIDIA GPU Node Leasing"
                            className="relative rounded-lg shadow-lg w-full object-cover hover:scale-105 transition-transform duration-500 group-hover/image:shadow-blue-500/25"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        {/* Performance Metrics */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-black/20 rounded-xl border border-blue-500/10">
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-blue-400 mb-1">30s</div>
                            <div className="text-xs text-gray-400">Deploy Time</div>
                          </div>
                          <div className="text-center border-x border-blue-500/20">
                            <div className="text-lg sm:text-2xl font-bold text-blue-400 mb-1">A100</div>
                            <div className="text-xs text-gray-400">GPU Access</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-blue-400 mb-1">∞</div>
                            <div className="text-xs text-gray-400">Scaling</div>
                          </div>
                        </div>
                        
                        {/* Learn More Button - Full Width */}
                        <Link 
                          to="/services/gpu-leasing" 
                          className="btn-futuristic w-full text-center flex items-center justify-center gap-2 group"
                          onClick={handlePageNavigation}
                        >
                          <span>Learn More</span>
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </Link>
                      </div>
                      
                      <div className="lg:order-2 space-y-6">
                        <div className="flex items-center mb-6">
                          <div className="mr-4">
                            <StyledIcon 
                              icon={Cloud} 
                              size="md" 
                              color="blue" 
                              variant="default"
                            />
                          </div>
                          <h3 className="text-2xl font-semibold text-white-gradient">
                            NVIDIA GPU Node Leasing
                          </h3>
                        </div>
                        
                        <p className="text-gray-300 leading-relaxed">
                          Flexible GPU computing resources available on-demand. Access powerful 
                          NVIDIA GPU nodes without the capital investment, perfect for projects 
                          with varying computational needs and scalable workloads.
                        </p>
                        
                        <FeatureList 
                          features={[
                            "Pay-as-you-use pricing model for cost efficiency",
                            "Instant provisioning and scaling capabilities",
                            "High-performance NVIDIA GPU access",
                            "Professional support and optimization services"
                          ]}
                          className="mb-6"
                        />
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>

                {/* On-Demand Cloud GPU Compute */}
                <motion.div variants={ANIMATION_VARIANTS.slideUp} id="on-demand-cloud-gpu-compute">
                  <GlassCard className="rounded-2xl p-8 border border-emerald-500/20 hover:border-emerald-400/30 transition-all duration-500 group">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div className="space-y-6">
                        <div className="flex items-center mb-6">
                          <div className="mr-4">
                            <StyledIcon 
                              icon={Settings} 
                              size="md" 
                              color="emerald" 
                              variant="default"
                            />
                          </div>
                          <h3 className="text-2xl font-semibold text-white-gradient">
                            On-Demand Cloud GPU Compute
                          </h3>
                        </div>
                        
                        <p className="text-gray-300 leading-relaxed">
                          Scalable cloud-based GPU computing resources that adapt to your needs in real-time. 
                          Whether you need additional compute power for specific projects or ongoing operations, 
                          our cloud solution provides the flexibility and performance you require.
                        </p>
                        
                        <FeatureList 
                          features={[
                            "Auto-scaling based on workload demands",
                            "Cloud-based deployment for maximum accessibility",
                            "Integrated monitoring and analytics dashboard",
                            "Flexible contract terms and pricing options"
                          ]}
                          className="mb-6"
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <div className="relative group/image">
                          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-transparent rounded-lg"></div>
                          <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                          <img
                            src="/assets/images/services/On_Demand_Compute.jpg"
                            alt="On-Demand Cloud GPU Compute"
                            className="relative rounded-lg shadow-lg w-full object-cover hover:scale-105 transition-transform duration-500 group-hover/image:shadow-emerald-500/25"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        {/* Cloud Performance Stats */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-black/20 rounded-xl border border-emerald-500/10">
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-emerald-400 mb-1">Auto</div>
                            <div className="text-xs text-gray-400">Scaling</div>
                          </div>
                          <div className="text-center border-x border-emerald-500/20">
                            <div className="text-lg sm:text-2xl font-bold text-emerald-400 mb-1">Global</div>
                            <div className="text-xs text-gray-400">Access</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-emerald-400 mb-1">Real-time</div>
                            <div className="text-xs text-gray-400">Analytics</div>
                          </div>
                        </div>
                        
                        {/* Learn More Button - Full Width */}
                        <Link 
                          to="/services/on-demand" 
                          className="btn-futuristic w-full text-center flex items-center justify-center gap-2 group"
                          onClick={handlePageNavigation}
                        >
                          <span>Learn More</span>
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>

                {/* AI Software Solutions */}
                <motion.div variants={ANIMATION_VARIANTS.slideUp} id="ai-software-solutions">
                  <GlassCard className="rounded-2xl p-8 border border-purple-500/20 hover:border-purple-400/30 transition-all duration-500 group">
                    <div className="grid lg:grid-cols-2 gap-8 items-center">
                      <div className="lg:order-1 space-y-4">
                        <div className="relative group/image">
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent rounded-lg"></div>
                          <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"></div>
                          <img
                            src="/assets/images/services/AI_Software_Solutions.jpg"
                            alt="AI Software Solutions"
                            className="relative rounded-lg shadow-lg w-full object-cover hover:scale-105 transition-transform duration-500 group-hover/image:shadow-purple-500/25"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-lg opacity-0 group-hover/image:opacity-100 transition-opacity duration-300"></div>
                        </div>
                        
                        {/* AI Development Stats */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 p-3 sm:p-4 bg-black/20 rounded-xl border border-purple-500/10">
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-purple-400 mb-1">Custom</div>
                            <div className="text-xs text-gray-400">AI Models</div>
                          </div>
                          <div className="text-center border-x border-purple-500/20">
                            <div className="text-lg sm:text-2xl font-bold text-purple-400 mb-1">ML</div>
                            <div className="text-xs text-gray-400">Pipelines</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg sm:text-2xl font-bold text-purple-400 mb-1">24/7</div>
                            <div className="text-xs text-gray-400">Support</div>
                          </div>
                        </div>
                        
                        {/* Learn More Button - Full Width */}
                        <Link 
                          to="/services/ai-solutions" 
                          className="btn-futuristic w-full text-center flex items-center justify-center gap-2 group"
                          onClick={handlePageNavigation}
                        >
                          <span>Learn More</span>
                          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </Link>
                      </div>
                      
                      <div className="lg:order-2 space-y-6">
                        <div className="flex items-center mb-6">
                          <div className="mr-4">
                            <StyledIcon 
                              icon={Zap} 
                              size="md" 
                              color="purple" 
                              variant="default"
                            />
                          </div>
                          <h3 className="text-2xl font-semibold text-white-gradient">
                            AI Software Solutions
                          </h3>
                        </div>
                        
                        <p className="text-gray-300 leading-relaxed">
                          Comprehensive AI software development and implementation services. 
                          From custom AI models to enterprise-grade applications, we deliver 
                          software solutions that drive business value and innovation.
                        </p>
                        
                        <FeatureList 
                          features={[
                            "Custom AI model development and training",
                            "Enterprise AI application integration",
                            "Machine learning pipeline development",
                            "Ongoing maintenance and optimization support"
                          ]}
                          className="mb-6"
                        />
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-8">
          {/* CTA Background - Rotated orientation for alternating effect */}
          <div 
            className={ctaBackgroundClasses}
            style={{
              ...ctaBackgroundStyle,
              transform: 'translateZ(0) rotate(180deg)',
              backgroundPosition: 'bottom',
            }}
          />
          
          <div className="container relative z-40">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={ANIMATION_VARIANTS.slideUp}
            >
              <GlassCard className="rounded-2xl p-8 text-center max-w-4xl mx-auto">
                <h2 className="heading-2 mb-6">Ready to Get <span className="text-primary-gradient">Started?</span></h2>
                <p className="body-large mb-8 text-gray-300">
                  Contact our team of experts to discuss your GPU computing and AI software needs. 
                  Discover how our high-performance solutions can accelerate your projects and drive innovation.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/contact"
                    className="btn-futuristic inline-flex items-center justify-center"
                    onClick={handlePageNavigation}
                  >
                    Get in Touch
                  </Link>
                  <Link
                    to="/about"
                    className="btn-outline inline-flex items-center justify-center"
                    onClick={handlePageNavigation}
                  >
                    Learn More About Us
                  </Link>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </section>
      </div>
    </motion.div>
  );
};

export default ServicesPage;
